import React, { useState, useMemo } from 'react';

interface LessonPlanSummaryItem {
  lessonPlanId: number;
  lessonPlanName: string;
  topicId: number;
  topicName: string;
  totalSteps: number;
  completedSteps: number;
  batchId: number;
  batchName: string;
}

interface LessonPlanOverviewProps {
  lessonPlanSummary: LessonPlanSummaryItem[];
  onOpenLessonPlan: (batchId: number, lessonPlanId: number, topicId?: number) => void;
}

type FilterMode = 'all' | 'in-progress' | 'completed' | 'not-started';

const LessonPlanOverview: React.FC<LessonPlanOverviewProps> = ({
  lessonPlanSummary,
  onOpenLessonPlan,
}) => {
  const [filterMode, setFilterMode] = useState<FilterMode>('all');
  const [searchQuery, setSearchQuery] = useState('');

  const getStatus = (item: LessonPlanSummaryItem): string => {
    if (item.totalSteps === 0) return 'not-started';
    if (item.completedSteps >= item.totalSteps) return 'completed';
    if (item.completedSteps > 0) return 'in-progress';
    return 'not-started';
  };

  const filtered = useMemo(() => {
    let result = [...lessonPlanSummary];

    if (filterMode !== 'all') {
      result = result.filter(item => getStatus(item) === filterMode);
    }

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(item =>
        (item.lessonPlanName || '').toLowerCase().includes(q) ||
        (item.topicName || '').toLowerCase().includes(q) ||
        (item.batchName || '').toLowerCase().includes(q)
      );
    }

    return result;
  }, [lessonPlanSummary, filterMode, searchQuery]);

  // Overall stats
  const stats = useMemo(() => {
    const total = lessonPlanSummary.length;
    const completed = lessonPlanSummary.filter(i => getStatus(i) === 'completed').length;
    const inProgress = lessonPlanSummary.filter(i => getStatus(i) === 'in-progress').length;
    const notStarted = lessonPlanSummary.filter(i => getStatus(i) === 'not-started').length;
    const totalSteps = lessonPlanSummary.reduce((sum, i) => sum + i.totalSteps, 0);
    const completedSteps = lessonPlanSummary.reduce((sum, i) => sum + i.completedSteps, 0);
    const overallPercent = totalSteps > 0 ? Math.round((completedSteps / totalSteps) * 100) : 0;
    return { total, completed, inProgress, notStarted, totalSteps, completedSteps, overallPercent };
  }, [lessonPlanSummary]);

  return (
    <div className="idash-lp-overview">
      {/* Progress summary */}
      <div className="lp-progress-summary">
        <div className="lp-progress-bar-container">
          <div className="lp-progress-label">
            Overall Progress: {stats.completedSteps}/{stats.totalSteps} steps ({stats.overallPercent}%)
          </div>
          <div className="lp-progress-bar">
            <div className="lp-progress-fill" style={{ width: `${stats.overallPercent}%` }} />
          </div>
        </div>
        <div className="lp-stat-chips">
          <span className="lp-stat-chip completed">{stats.completed} Completed</span>
          <span className="lp-stat-chip in-progress">{stats.inProgress} In Progress</span>
          <span className="lp-stat-chip not-started">{stats.notStarted} Not Started</span>
        </div>
      </div>

      {/* Filters */}
      <div className="lp-filters">
        <div className="lp-filter-tabs">
          {(['all', 'in-progress', 'completed', 'not-started'] as FilterMode[]).map(mode => (
            <button
              key={mode}
              className={`lp-filter-tab ${filterMode === mode ? 'active' : ''}`}
              onClick={() => setFilterMode(mode)}
            >
              {mode === 'all' ? 'All' : mode === 'in-progress' ? 'In Progress' : mode === 'completed' ? 'Completed' : 'Not Started'}
            </button>
          ))}
        </div>
        <input
          type="text"
          className="lp-search"
          placeholder="Search lesson plans..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Lesson plan table */}
      {filtered.length === 0 ? (
        <div className="lp-empty">
          No lesson plans found{filterMode !== 'all' ? ` with status "${filterMode}"` : ''}.
        </div>
      ) : (
        <div className="lp-table">
          <div className="lp-table-header">
            <div className="lp-col-name">Lesson Plan</div>
            <div className="lp-col-topic">Topic</div>
            <div className="lp-col-batch">Batch</div>
            <div className="lp-col-progress">Progress</div>
            <div className="lp-col-status">Status</div>
            <div className="lp-col-action">Action</div>
          </div>
          {filtered.map((item, idx) => {
            const status = getStatus(item);
            const percent = item.totalSteps > 0 ? Math.round((item.completedSteps / item.totalSteps) * 100) : 0;

            return (
              <div key={`${item.lessonPlanId}-${item.batchId}-${idx}`} className="lp-table-row">
                <div className="lp-col-name">{item.lessonPlanName || '-'}</div>
                <div className="lp-col-topic">{item.topicName || '-'}</div>
                <div className="lp-col-batch">{item.batchName || '-'}</div>
                <div className="lp-col-progress">
                  <div className="lp-mini-progress">
                    <div className="lp-mini-bar">
                      <div className={`lp-mini-fill ${status}`} style={{ width: `${percent}%` }} />
                    </div>
                    <span className="lp-mini-text">{item.completedSteps}/{item.totalSteps}</span>
                  </div>
                </div>
                <div className="lp-col-status">
                  <span className={`lp-status-chip ${status}`}>
                    {status === 'completed' ? 'Completed' : status === 'in-progress' ? 'In Progress' : 'Not Started'}
                  </span>
                </div>
                <div className="lp-col-action">
                  <button
                    className="lp-open-btn"
                    onClick={() => onOpenLessonPlan(item.batchId, item.lessonPlanId, item.topicId)}
                  >
                    Open Lesson Plan
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default LessonPlanOverview;
